import React from 'react';
import { Table, Button, Space, Tooltip, Popconfirm, Avatar } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import PartnerStatusBadge from '../shared/PartnerStatusBadge';
import { useUpdatePartner } from '../../hooks';
import { getInitials, getTablePaginationConfig } from '../../utils/helpers';
import moment from 'moment';

const nextStatus = (current) => (current === 'active' ? 'inactive' : 'active');

const PartnerTable = ({ data, loading, pagination, onPaginationChange, onDelete }) => {
  const history = useHistory();
  const updatePartner = useUpdatePartner();

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      width: 80,
      render: (logo, record) => (
        <Avatar
          size={48}
          src={logo}
          icon={!logo && <UserOutlined />}
          style={{ backgroundColor: logo ? 'transparent' : '#1890ff' }}
        >
          {!logo && getInitials(record.name)}
        </Avatar>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (name, record) => (
        <Tooltip placement="topLeft" title={name}>
          <Button
            type="link"
            onClick={() => history.push(`/b2b/partners/${record.id}`)}
            style={{ padding: 0, maxWidth: '100%', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {name}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (email) => (
        <Tooltip placement="topLeft" title={email}>
          {email}
        </Tooltip>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Suspended', value: 'suspended' },
      ],
      render: (status, record) => (
        <Popconfirm
          title={`Set to ${nextStatus(status)}?`}
          onConfirm={() => updatePartner.mutate({ id: record.id, data: { status: nextStatus(status) } })}
          okText="Yes"
          cancelText="No"
        >
          <PartnerStatusBadge status={status} style={{ cursor: 'pointer' }} />
        </Popconfirm>
      ),
    },
    {
      title: 'Clinics',
      dataIndex: 'clinics_count',
      key: 'clinics_count',
      width: 100,
      align: 'center',
      render: (count) => count || 0,
    },
    {
      title: 'Doctors',
      dataIndex: 'doctors_count',
      key: 'doctors_count',
      width: 100,
      align: 'center',
      render: (count) => count || 0,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: true,
      render: (date) => moment(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EyeOutlined />}
              onClick={() => history.push(`/b2b/partners/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => history.push(`/b2b/partners/${record.id}/edit`)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this partner?"
            description="This action cannot be undone and will remove all associated data."
            onConfirm={() => onDelete?.(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="default" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const paginationConfig = pagination
    ? getTablePaginationConfig(pagination, onPaginationChange)
    : false;

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={paginationConfig}
      rowKey="id"
      scroll={{ x: 1260 }}
      size="middle"
    />
  );
};

export default PartnerTable;
